import { motion } from "framer-motion";
import { Disc, ListMusic, Music2, Sparkles, Users } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { useProfileStore } from "../stores/profileStore";

interface WelcomeCardProps {
  playlists: number;
  albums: number;
  tracks: number;
  artists: number;
  listenerType: string;
}

export const WelcomeCard: React.FC<WelcomeCardProps> = ({ playlists, albums, tracks, artists, listenerType }) => {
  const { profile, updateProfile } = useProfileStore();

  const handleNameChange = (e: React.FormEvent<HTMLSpanElement>) => {
    const newName = e.currentTarget.textContent?.trim() || "friend";
    updateProfile({ name: newName });
  };

  const stats = [
    { icon: ListMusic, value: playlists, label: "Playlists", link: "/playlists" },
    { icon: Disc, value: albums, label: "Albums", link: "/albums" },
    { icon: Music2, value: tracks, label: "Tracks", link: "/library" },
    { icon: Users, value: artists, label: "Artists", link: "/statistics" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-lg"
    >
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-blue-800/10 to-black/80 animate-gradient" />
        <div className="absolute inset-0 backdrop-blur-3xl" />
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#2196f321]/20 rounded-full blur-3xl transform translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl transform -translate-x-1/2 translate-y-1/2" />
      </div>

      {/* Content */}
      <div className="relative bg-zinc-800/80 backdrop-blur-sm border border-white/5 rounded-lg p-8">
        <div className="max-w-4xl mx-auto md:mx-0">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className="flex-1">
              <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-white via-white to-zinc-400 bg-clip-text text-transparent flex flex-wrap gap-2">
                Welcome back,{" "}
                <span
                  contentEditable
                  onBlur={handleNameChange}
                  suppressContentEditableWarning
                  className="bg-white/10 px-3 py-1 rounded-md focus:outline-none focus:ring-2 focus:ring-yellow-500 inline-block min-w-[80px] max-w-[200px] overflow-hidden text-ellipsis whitespace-nowrap"
                >
                  {profile.name}
                </span>
              </h1>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-yellow-500" />
                <span className="text-zinc-400">
                  You're what we'd call a{" "}
                  <Link
                    to="/statistics"
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-500/10 text-yellow-500 rounded-md hover:bg-yellow-500/20 transition"
                  >
                    {listenerType}
                  </Link>
                </span>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {stats.map((stat, index) => (
              <Link
                key={stat.label}
                to={stat.link}
                className="group relative bg-white/5 rounded-lg p-4 hover:bg-white/10 transition-all duration-300"
              >
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-center gap-3"
                >
                  <div className="p-2 rounded-lg bg-white/5 group-hover:bg-white/10 transition-colors">
                    <stat.icon className="w-5 h-5 text-yellow-500" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{stat.value}</div>
                    <div className="text-sm text-zinc-400">{stat.label}</div>
                  </div>
                </motion.div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
